'use client';

import { useTranslation } from 'react-i18next';
import { Lock, ArrowLeft, Mail } from 'lucide-react';
import Link from 'next/link';

export default function PrivacyPage() {
  const { t } = useTranslation('common');

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-12 md:py-20">
        
        <Link href="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" />
          {t('blog.back')}
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 bg-primary/10 rounded-xl">
            <Lock className="w-6 h-6 text-primary" />
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-foreground">{t('legal.privacy_title')}</h1>
        </div>

        <p className="text-sm text-muted-foreground mb-8">{t('legal.last_updated')}: 2026-04-04</p>

        <div className="prose prose-invert max-w-none space-y-6 text-foreground/90 leading-relaxed">
          <p className="text-lg">{t('legal.privacy_intro')}</p>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-3">1. מידע שאנו אוספים</h2>
            <p>
              <strong>מידע שנמסר על ידך:</strong> שם, כתובת דואר אלקטרוני, מספר טלפון, תמונות פרופיל, תיאור שירותים.
            </p>
            <p>
              <strong>מידע נאסף אוטומטית:</strong> כתובת IP, סוג דפדפן ומכשיר, עמודים שנצפו, זמן שהייה באתר.
            </p>
            <p>
              <strong>מידע מצדדים שלישיים:</strong> נתונים מחשבון Google בעת הרשמה באמצעות Google Sign-In.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-3">2. כיצד אנו משתמשים במידע</h2>
            <ul className="list-disc list-inside space-y-2">
              <li>מתן שירותי הפלטפורמה ושיפורם</li>
              <li>התאמה אישית של חוויית המשתמש</li>
              <li>תקשורת עם משתמשים (הודעות מערכת, עדכונים)</li>
              <li>אבטחת מידע ומניעה של פעילות חשודה</li>
              <li>ניתוח סטטיסטי ושיפור ביצועים</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-3">3. שיתוף מידע</h2>
            <p>
              אנו לא מוכרים, משכירים או מעבירים את המידע האישי שלך לצדדים שלישיים, למעט:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>ספקי שירות טכניים (Firebase/Google Cloud) — לצורך תפעול הפלטפורמה</li>
              <li>דרישה חוקית — צו בית משפט או חקירה רשמית</li>
              <li>הסכמתך המפורשת</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-3">4. אבטחת מידע</h2>
            <p>
              אנו נוקטים אמצעי אבטחה סבירים להגנה על המידע שלך, כולל:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>הצפנת תקשורת (HTTPS/TLS)</li>
              <li>אימות דו-שלבי עבור ממשקי ניהול</li>
              <li>גישה מוגבלת לנתונים — רק עובדים מורשים</li>
              <li>שימוש בשירותי Firebase Auth ו-Firestore Security Rules</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-3">5. זכויותיך (בהתאם לחוק הגנת הפרטיות, התשמ&quot;א-1981)</h2>
            <p>עומדות לרשותך הזכויות הבאות:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>זכות עיון — לעיין במידע שנאסף עליך</li>
              <li>זכות תיקון — לבקש תיקון מידע שגוי</li>
              <li>זכות מחיקה — לבקש מחיקת חשבונך ומידע הקשור אליו</li>
              <li>זכות לינוידות — לבקש העברת הנתונים שלך</li>
            </ul>
            <p>
              לשם מימוש זכויות אלה, פנה אלינו בכתובת: <strong>support@yofiplace.co.il</strong>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-3">6. עוגיות (Cookies)</h2>
            <p>
              האתר משתמש בעוגיות לצורך אימות משתמשים, שמירת העדפות שפה ואנליטיקה בסיסית.
              באמצעות המשך השימוש באתר, אתה מסכים לשימוש בעוגיות.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-3">7. שינויים במדיניות</h2>
            <p>
              אנו עשויים לעדכן מדיניות זו מעת לעת. שינויים מהותיים יפורסמו באתר.
              המשך השימוש לאחר עדכון מהווה הסכמה למדיניות המעודכנת.
            </p>
          </section>

          <section className="mt-10 p-4 bg-primary/5 border border-primary/20 rounded-xl">
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Mail className="w-4 h-4" />
              {t('legal.contact_us')}: support@yofiplace.co.il
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
